"use client";

import { Link } from "@/navigation";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocale } from "next-intl";
import { useForm, SubmitHandler } from "react-hook-form";
import { useUserAuth } from "@/lib/hooks/useUserAuth";
import { useCreateUser } from "@/lib/hooks/user/useCreateUser";
import { useUserStore } from "@/store/user.store";
import { setCookie, deleteCookie, getCookie } from "cookies-next";
import { message } from "antd";
import { ProductCategoriesDocument } from "@/gql/graphql";
import { executeGraphQL } from "@/lib/graphql";
import { handleGraphqlLocale } from "@/lib/utils/util";
import { defaultLocale } from "@/config";
import { useChannel } from "@/context/channel";
import { getCollectionProducts } from "@/lib/api/product";
import { filterCateImg } from "@/components/Home2/Collection";

const SLIDES = [
	{
		id: "banner1",
		image: "/image/gykj/banner1.png",
		href: "/products",
	},
	{
		id: "banner2",
		image: "/image/gykj/banner2.png",
		href: "/products",
	},
];

type FlashItem = {
	id: string;
	image: string;
	href: string;
	discount: string;
	price: string;
	originalPrice: string;
	/** Saleor gross.currency，缺省按 USD 展示 */
	currency?: string;
};

const BANNER_IMG = SLIDES[0].image;

const DEFAULT_FLASH_ITEMS: FlashItem[] = [
	{ id: "f1", image: BANNER_IMG, href: "/products", discount: "-20%", price: "3.01", originalPrice: "3.76" },
	{ id: "f2", image: BANNER_IMG, href: "/products", discount: "-20%", price: "1.58", originalPrice: "1.98" },
	{ id: "f3", image: BANNER_IMG, href: "/products", discount: "-20%", price: "3.48", originalPrice: "4.35" },
	{
		id: "f4",
		image: BANNER_IMG,
		href: "/products",
		discount: "-20%",
		price: "17.18",
		originalPrice: "21.48",
	},
	{
		id: "f5",
		image: BANNER_IMG,
		href: "/products",
		discount: "-20%",
		price: "19.77",
		originalPrice: "24.71",
	},
	{ id: "f6", image: BANNER_IMG, href: "/products", discount: "-20%", price: "6.68", originalPrice: "8.35" },
	{
		id: "f7",
		image: BANNER_IMG,
		href: "/products",
		discount: "-20%",
		price: "10.91",
		originalPrice: "13.64",
	},
	{ id: "f8", image: BANNER_IMG, href: "/products", discount: "-20%", price: "8.22", originalPrice: "10.28" },
];

function productToFlashItem(product: any, locale: string): FlashItem {
	const name = locale === defaultLocale ? product.name : product.translation?.name || product.name;
	const imgUrl =
		filterCateImg(product.metadata)[2]?.url ||
		filterCateImg(product.metadata)[0]?.url ||
		"/image/default-image.webp";
	const variant = product.variants?.[0];
	const price = variant?.pricing?.price?.gross?.amount as number | undefined;
	const currency = (variant?.pricing?.price?.gross?.currency as string | undefined) || "USD";
	const compareAt = price ? Number((price * 1.18).toFixed(2)) : undefined;
	const discount = price && compareAt ? Math.round(((compareAt - price) / compareAt) * 100) : 20;
	return {
		id: product.slug || name,
		image: imgUrl,
		href: product.slug ? `/product/${product.slug}` : "/products",
		discount: `-${discount}%`,
		price: price ? price.toFixed(2) : "0.00",
		originalPrice: compareAt ? compareAt.toFixed(2) : "0.00",
		currency,
	};
}

function getFlashSaleEndMs(): number {
	const now = new Date();
	const end = new Date(now);
	const dow = end.getDay();
	const daysUntilSunday = dow === 0 ? 0 : 7 - dow;
	end.setDate(end.getDate() + daysUntilSunday);
	end.setHours(23, 59, 59, 999);
	if (end.getTime() <= now.getTime()) {
		end.setDate(end.getDate() + 7);
	}
	return end.getTime();
}

function FlashCountdown() {
	const t = useTranslations("homeBeauty");
	const [parts, setParts] = useState({ h: 0, m: 0, s: 0 });
	const endRef = useRef(getFlashSaleEndMs());

	useEffect(() => {
		const tick = () => {
			const ms = Math.max(0, endRef.current - Date.now());
			const totalSec = Math.floor(ms / 1000);
			setParts({
				h: Math.floor(totalSec / 3600),
				m: Math.floor((totalSec % 3600) / 60),
				s: totalSec % 60,
			});
		};
		tick();
		const id = setInterval(tick, 1000);
		return () => clearInterval(id);
	}, []);

	const pad = (n: number) => String(n).padStart(2, "0");
	const hStr = String(parts.h);
	const labels = [t("flashDealsCountdownH"), t("flashDealsCountdownM"), t("flashDealsCountdownS")] as const;
	const values = [hStr, pad(parts.m), pad(parts.s)] as const;

	return (
		<div className="flex items-center gap-2 sm:gap-2.5" aria-live="polite" aria-atomic="true">
			{values.map((v, i) => (
				<div key={labels[i]} className="flex items-center gap-1 sm:gap-1.5">
					<span className="bg-brand font-body flex min-h-[26px] min-w-[28px] items-center justify-center rounded-md px-1.5 text-[13px] font-bold tabular-nums text-white sm:min-h-[28px] sm:min-w-[32px] sm:text-[14px]">
						{v}
					</span>
					<span className="font-body text-brand-muted/90 text-[11px] font-medium uppercase tracking-wide sm:text-[12px]">
						{labels[i]}
					</span>
				</div>
			))}
		</div>
	);
}

function FlameIcon({ className }: { className?: string }) {
	return (
		<svg
			className={className}
			width="14"
			height="14"
			viewBox="0 0 24 24"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			aria-hidden
		>
			<path
				d="M12 2s2.5 4.5 2.5 8c0 2-1.2 3.6-2.5 4.5-1.3-.9-2.5-2.5-2.5-4.5 0-3.5 2.5-8 2.5-8z"
				fill="oklch(0.55 0.2 25)"
			/>
			<path
				d="M8.5 10c0 2.8 2 5 3.5 6 1.5-1 3.5-3.2 3.5-6 0-2.5-1.5-5.5-2.2-7-.7 1.5-2.3 4.2-2.3 7z"
				fill="oklch(0.62 0.22 35)"
			/>
			<path
				d="M11 14.5c.8.9 1.5 1.8 1.9 2.5.6-.8 1.1-2.1 1.1-3.5 0-1.2-.4-2.4-.8-3.3-.4 1-.7 2.2-.7 3.5 0 .5.1.9.2 1.3-.3-.4-.6-.9-.7-1.5z"
				fill="oklch(0.72 0.18 50)"
			/>
		</svg>
	);
}

function GoogleIcon() {
	return (
		<svg width="14" height="14" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" aria-hidden>
			<path
				fill="#EA4335"
				d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
			/>
			<path
				fill="#4285F4"
				d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
			/>
			<path
				fill="#FBBC05"
				d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
			/>
			<path
				fill="#34A853"
				d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
			/>
		</svg>
	);
}

type LoginFormValues = {
	email?: string;
	password?: string;
};

type SignUpFormValues = {
	email?: string;
	password?: string;
	confirmPassword?: string;
	locale?: string;
};

function AuthCard() {
	const t = useTranslations("homeBeauty");
	const tCommon = useTranslations();
	const [mode, setMode] = useState<"login" | "register">("register");
	const [googleLoading, setGoogleLoading] = useState(false);
	const locale = useLocale();

	const { useLogin, useSignOut } = useUserAuth();
	const { createUser, loading: signUpLoading } = useCreateUser();
	const { userInfo, setuserInfo } = useUserStore();

	let get_form_info: any = getCookie("created__user__info");
	if (get_form_info) {
		get_form_info = JSON.parse(get_form_info);
	}

	const loginForm = useForm<LoginFormValues>();
	const signUpForm = useForm<SignUpFormValues>();

	const [loginLoading, setLoginLoading] = useState(false);

	const onLoginSubmit: SubmitHandler<LoginFormValues> = async (data) => {
		setLoginLoading(true);
		try {
			await useLogin(data as { email: string; password: string });
			loginForm.reset();
		} catch (e: any) {
			message.error(e?.message || "error");
		} finally {
			setLoginLoading(false);
		}
	};

	const onSignUpSubmit: SubmitHandler<SignUpFormValues> = (data) => {
		data.locale = locale;
		createUser(data, signUpForm.reset, () => setMode("login"));
	};

	const handleGoogleLogin = async () => {
		const googleLoginUrl = process.env.NEXT_PUBLIC_GOOGLE_LOGIN_URL;
		if (!googleLoginUrl) return;
		try {
			setGoogleLoading(true);
			setCookie("googleLoginRedirectPath", window.location.href);
			const res = await fetch(googleLoginUrl);
			const data = (await res.json()) as any;
			window.location.href = data.detail.approval_url;
		} catch (error) {
			console.error("Google login error:", error);
			setGoogleLoading(false);
		}
	};

	const handleLogout = () => {
		deleteCookie("__user__login__info");
		deleteCookie("user-store");
		useSignOut();
		setuserInfo(undefined);
		message.success(t("logoutSuccess"));
	};

	if (userInfo?.token || userInfo?.email) {
		const displayEmail = userInfo.email || userInfo.user?.email || "";
		const initial = displayEmail.charAt(0).toUpperCase();
		return (
			<div className="flex h-full flex-col items-center justify-center bg-white px-5">
				<div className="bg-brand font-display flex h-10 w-10 items-center justify-center rounded-full text-[18px] font-bold text-white">
					{initial}
				</div>
				<p className="font-body text-brand-ink mt-2.5 max-w-full truncate text-[13px] font-medium">
					{t("welcomeUser", { email: displayEmail })}
				</p>
				<Link
					href="/center"
					className="bg-brand font-body mt-3 flex h-8 w-full items-center justify-center text-[12px] font-bold uppercase tracking-[0.08em] text-white transition-opacity hover:opacity-90"
				>
					{t("myAccount")}
				</Link>
				<button
					type="button"
					onClick={handleLogout}
					className="font-body text-brand-muted hover:text-brand-ink mt-2 text-[12px] font-medium transition-colors"
				>
					{t("logoutBtn")}
				</button>
			</div>
		);
	}

	const loading = mode === "login" ? loginLoading : signUpLoading;

	const inputBase =
		"border-brand-ink/18 h-8 w-full border bg-brand-tint px-3 font-body text-[13px] text-brand-ink outline-none transition-colors focus:border-brand";
	const inputError = "!border-red-500";

	return (
		<div className="flex h-full flex-col bg-white">
			<div className="px-5 pt-5">
				<h3 className="font-display text-brand-ink text-[17px] leading-tight xl:text-[18px]">
					{mode === "register" ? t("registerTitle") : t("loginTitle")}
				</h3>
				<span className="font-body text-brand-fg text-[13px] xl:text-[14px]">
					{mode === "register" ? t("registerSubtitle") : t("loginSubtitle")}
				</span>
			</div>

			{mode === "login" ? (
				<form className="flex flex-col gap-2.5 px-5 pt-4" onSubmit={loginForm.handleSubmit(onLoginSubmit)}>
					<input
						type="text"
						placeholder={t("registerEmail")}
						defaultValue={get_form_info?.email}
						className={`${inputBase} ${loginForm.formState.errors.email ? inputError : ""}`}
						{...loginForm.register("email", { required: true })}
					/>
					<input
						type="password"
						placeholder={t("registerPassword")}
						className={`${inputBase} ${loginForm.formState.errors.password ? inputError : ""}`}
						{...loginForm.register("password", { required: true })}
					/>
					<button
						type="submit"
						disabled={loginLoading}
						className="bg-brand font-body mt-1 flex h-9 w-full items-center justify-center text-[13px] font-bold uppercase tracking-[0.1em] text-white transition-opacity hover:opacity-90 disabled:opacity-60"
					>
						{loginLoading ? (
							<span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
						) : (
							t("loginBtn")
						)}
					</button>
				</form>
			) : (
				<form className="flex flex-col gap-2.5 px-5 pt-4" onSubmit={signUpForm.handleSubmit(onSignUpSubmit)}>
					<input
						type="email"
						placeholder={t("registerEmail")}
						className={`${inputBase} ${signUpForm.formState.errors.email ? inputError : ""}`}
						{...signUpForm.register("email", { required: true })}
					/>
					<input
						type="password"
						placeholder={t("registerPassword")}
						className={`${inputBase} ${signUpForm.formState.errors.password ? inputError : ""}`}
						{...signUpForm.register("password", { required: true })}
					/>
					<input
						type="password"
						placeholder={t("registerConfirm")}
						className={`${inputBase} ${signUpForm.formState.errors.confirmPassword ? inputError : ""}`}
						{...signUpForm.register("confirmPassword", { required: true })}
					/>
					<button
						type="submit"
						disabled={signUpLoading}
						className="bg-brand font-body mt-1 flex h-9 w-full items-center justify-center text-[13px] font-bold uppercase tracking-[0.1em] text-white transition-opacity hover:opacity-90 disabled:opacity-60"
					>
						{signUpLoading ? (
							<span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
						) : (
							t("registerBtn")
						)}
					</button>
				</form>
			)}

			{/* Divider */}
			<div className="relative mx-5 my-2.5">
				<div className="absolute inset-0 flex items-center">
					<div className="border-brand-ink/10 w-full border-t" />
				</div>
				<div className="relative flex justify-center">
					<span className="font-body text-brand-muted bg-white px-2 text-[11px]">{t("orDivider")}</span>
				</div>
			</div>

			{/* Google Login */}
			<div className="px-5">
				<button
					type="button"
					onClick={handleGoogleLogin}
					disabled={googleLoading}
					className="border-brand-ink/15 font-body text-brand-ink hover:bg-brand-tint flex h-8 w-full items-center justify-center gap-2 border bg-white text-[12px] font-medium transition-colors disabled:opacity-60"
				>
					{googleLoading ? (
						<span className="border-brand inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-t-transparent" />
					) : (
						<GoogleIcon />
					)}
					{googleLoading ? t("googleLoginLoading") : t("googleLoginBtn")}
				</button>
			</div>

			<div className="mt-auto px-5 pb-4 pt-3 text-center">
				<span className="font-body text-brand-muted text-[12px]">
					{mode === "register" ? t("registerLogin") : t("loginRegister")}{" "}
					<button
						type="button"
						onClick={() => {
							setMode(mode === "register" ? "login" : "register");
							loginForm.reset();
							signUpForm.reset();
						}}
						className="text-brand-fg font-semibold hover:underline"
					>
						{mode === "register" ? t("registerLoginLink") : t("loginRegisterLink")}
					</button>
				</span>
			</div>
		</div>
	);
}

const INTERVAL = 5500;

type SideMenuItem = { name: string; slug: string };

export default function HeroBanner() {
	const t = useTranslations("homeBeauty");
	const locale = useLocale();
	const [idx, setIdx] = useState(0);
	const [paused, setPaused] = useState(false);
	const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const [sideMenuItems, setSideMenuItems] = useState<SideMenuItem[]>([]);
	const { channel: flashChannel } = useChannel(locale);
	const [flashProducts, setFlashProducts] = useState<any[] | null>(null);

	const next = useCallback(() => setIdx((i) => (i + 1) % SLIDES.length), []);

	useEffect(() => {
		if (!flashChannel) return;
		getCollectionProducts({ slug: "flash-deals", locale, channel: flashChannel }).then((res) => {
			const edges = res?.collection?.products?.edges ?? [];
			setFlashProducts(edges.map((e: any) => e.node));
		});
	}, [flashChannel, locale]);

	useEffect(() => {
		if (paused) return;
		timer.current = setTimeout(next, INTERVAL);
		return () => {
			if (timer.current) clearTimeout(timer.current);
		};
	}, [idx, paused, next]);

	useEffect(() => {
		(async () => {
			try {
				const data = await executeGraphQL(ProductCategoriesDocument, {
					withAuth: false,
					variables: { locale: handleGraphqlLocale(locale), first: 20 },
				});
				const items =
					data?.categories?.edges?.map((e) => ({
						name: locale === defaultLocale ? e.node.name : e.node.translation?.name || e.node.name,
						slug: e.node.slug,
					})) ?? [];
				setSideMenuItems(items);
			} catch (err) {
				console.error("[HeroBanner] failed to fetch categories", err);
			}
		})();
	}, [locale]);

	return (
		<section className="w-full bg-[#f5f5f5] pb-4">
			<div className="mx-auto w-full max-w-[1580px] px-5 pt-3 sm:px-8 lg:px-12">
				{/* === Three-Column Hero === */}
				<div className="flex gap-0 lg:gap-3">
					{/* Left: Category Side Menu */}
					<nav className="hidden w-[200px] shrink-0 lg:block">
						<div className="h-full bg-white">
							<ul className="flex flex-col py-2">
								{sideMenuItems.map((item) => (
									<li key={item.slug}>
										<Link
											href={`/products/${item.slug}` as any}
											className="font-body text-brand-ink/85 hover:bg-brand/5 hover:!text-brand-fg flex items-center justify-between px-5 py-[9px] text-[14px] transition-colors duration-150 xl:text-[15px]"
										>
											<span>{item.name}</span>
											<svg
												width="6"
												height="10"
												viewBox="0 0 6 10"
												fill="none"
												className="shrink-0 opacity-30"
												aria-hidden
											>
												<path
													d="M1 1l4 4-4 4"
													stroke="currentColor"
													strokeWidth="1.2"
													strokeLinecap="round"
													strokeLinejoin="round"
												/>
											</svg>
										</Link>
									</li>
								))}
							</ul>
						</div>
					</nav>

					{/* Center: Carousel Banner */}
					<div
						className="relative min-h-[280px] flex-1 overflow-hidden sm:min-h-[340px] md:min-h-[380px]"
						onMouseEnter={() => setPaused(true)}
						onMouseLeave={() => setPaused(false)}
					>
						{SLIDES.map((sl, i) => (
							<Link
								key={sl.id}
								href={sl.href as any}
								aria-hidden={i !== idx}
								className={`absolute inset-0 transition-opacity duration-700 ease-out ${
									i === idx ? "opacity-100" : "pointer-events-none opacity-0"
								}`}
							>
								<img
									src={encodeURI(sl.image)}
									alt=""
									className="h-full w-full object-cover"
									loading={i === 0 ? "eager" : "lazy"}
									decoding="async"
									draggable={false}
								/>
							</Link>
						))}

						{/* Carousel indicators */}
						<div className="absolute bottom-4 left-1/2 z-10 flex -translate-x-1/2 gap-2">
							{SLIDES.map((sl, i) => (
								<button
									key={sl.id}
									type="button"
									onClick={(e) => {
										e.preventDefault();
										setIdx(i);
									}}
									aria-label={`Slide ${i + 1}`}
									className={`h-[6px] rounded-full transition-all duration-300 ${
										i === idx ? "w-6 bg-white" : "w-[6px] bg-white/50"
									}`}
								/>
							))}
						</div>

						{/* Nav arrows */}
						<button
							type="button"
							onClick={() => setIdx((i) => (i - 1 + SLIDES.length) % SLIDES.length)}
							className="absolute left-2 top-1/2 z-10 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-white/20 text-white backdrop-blur-sm transition-colors hover:bg-white/40"
							aria-label="Previous slide"
						>
							<svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
								<path
									d="M8 2L4 6l4 4"
									stroke="currentColor"
									strokeWidth="1.5"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
						</button>
						<button
							type="button"
							onClick={next}
							className="absolute right-2 top-1/2 z-10 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-white/20 text-white backdrop-blur-sm transition-colors hover:bg-white/40"
							aria-label="Next slide"
						>
							<svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
								<path
									d="M4 2l4 4-4 4"
									stroke="currentColor"
									strokeWidth="1.5"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
						</button>
					</div>

					{/* Right: Auth Card */}
					<aside className="hidden w-[220px] shrink-0 lg:block">
						<AuthCard />
					</aside>
				</div>

				{/* === Flash Deals Strip === */}
				<div className="mt-3 flex gap-3">
					{/* Flash Deals main area — bordered promo rail (reference layout) */}
					<div className="border-brand from-brand-softer/65 via-brand-tint/90 flex-1 overflow-hidden rounded-2xl border-[3px] bg-gradient-to-b to-white p-3 sm:p-4">
						<div className="mb-3 flex flex-col gap-3 sm:mb-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-x-4 sm:gap-y-2">
							<div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-center sm:gap-6">
								<h3 className="font-display text-brand-ink shrink-0 text-[18px] font-bold leading-tight sm:text-[19px] xl:text-[20px]">
									{t("flashDealsTitle")}
								</h3>
								<FlashCountdown />
							</div>
							<div className="flex justify-end sm:justify-end">
								<div className="inline-flex max-w-full items-center rounded-full bg-[oklch(0.96_0.06_85)] py-1.5 pl-3 pr-2.5 sm:pl-4 sm:pr-3">
									<span className="text-brand-ink/88 font-body text-[12px] font-medium leading-snug sm:text-[13px] xl:text-[14px]">
										{t("flashDealsTimer")}{" "}
										<span className="text-brand-ink/75 whitespace-nowrap">{t("flashDealsTimerChevron")}</span>
									</span>
								</div>
							</div>
						</div>

						<div className="scrollbar-none -mx-1 flex gap-1 overflow-x-auto px-1 pb-0.5 sm:gap-2">
							{(flashProducts && flashProducts.length > 0
								? flashProducts.map((p) => productToFlashItem(p, locale))
								: DEFAULT_FLASH_ITEMS
							).map((item) => (
								<Link
									key={item.id}
									href={item.href as any}
									className="group flex w-[108px] shrink-0 flex-col items-stretch sm:w-[118px]"
								>
									<div className="ring-brand-ink/[0.06] group-hover:ring-brand/25 relative mb-2 aspect-square w-full overflow-hidden rounded-lg bg-white shadow-[0_1px_0_oklch(0.88_0.02_55_/_0.35)] ring-1 transition-transform duration-200">
										<span className="bg-brand font-body absolute left-1 top-1 z-[1] rounded-sm px-1 py-0.5 text-[10px] font-bold leading-none text-white shadow-sm sm:text-[11px]">
											{item.discount}
										</span>
										<img
											src={encodeURI(item.image)}
											alt={t("flashDealsProductAlt")}
											className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
											loading="lazy"
											decoding="async"
											draggable={false}
										/>
									</div>
									<div className="flex min-h-[2rem] items-start gap-1">
										<FlameIcon className="mt-0.5 shrink-0" />
										<div className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-1.5 gap-y-0">
											<span className="font-body text-[13px] font-bold leading-tight text-[oklch(0.52_0.2_25)] sm:text-[14px]">
												{item.currency ?? "USD"} {item.price}
											</span>
											<span className="font-body text-brand-muted/70 text-[10px] leading-none line-through sm:text-[11px]">
												{item.currency ?? "USD"} {item.originalPrice}
											</span>
										</div>
									</div>
								</Link>
							))}
						</div>
					</div>

					{/* New User Coupon Card (beside Flash Deals) */}
					<div className="hidden w-[220px] shrink-0 lg:block">
						<div className="flex h-full flex-col items-center justify-center bg-white px-4 py-3">
							<span className="bg-brand font-body inline-block rounded px-3 py-1 text-[11px] font-bold uppercase tracking-[0.08em] text-white">
								{t("couponBadgeTitle")}
							</span>
							<p className="font-body text-brand-muted mt-2 text-center text-[12px] leading-snug">
								{t("couponBadgeDesc")}
							</p>
							<p className="font-body text-brand-ink/60 mt-1 text-center text-[11px] leading-snug">
								{t("couponBadgeNote")}
							</p>
							<div className="mt-3 flex items-center gap-2">
								<span className="font-display text-brand text-[30px] leading-none xl:text-[32px]">
									US{t("couponBadgeAmount")}
								</span>
								<Link
									href="/products"
									className="bg-brand font-body inline-flex items-center px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-white transition-opacity hover:!text-white hover:opacity-90"
								>
									{t("couponBadgeGetCoupon")}
								</Link>
							</div>
							<Link
								href="/products"
								className="font-body text-brand-fg mt-1.5 text-[12px] font-medium underline underline-offset-2 hover:opacity-80"
							>
								{t("couponBadgeCta")}
							</Link>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}
